import React, { useContext } from 'react';
import { Sequence } from 'remotion';
import { TextItemEditingContext } from '../context-provider';
import { EditModeContext } from '../edit-mode';
import { resolveGroupGeometry } from '../items/group/group-geometry';
import { EditorStarterItem } from '../items/item-type';
import { useAllItems, useSelectedItems, useTracks } from '../utils/use-context';
import { SelectionOutline } from './selection-outline';
import { GroupSelectionOutline } from './group-selection-outline';

const hideOutlinesForItemTypes: EditorStarterItem['type'][] = ['audio'];

export const SortedOutlines: React.FC = () => {
  const { tracks } = useTracks();
  const { selectedItems: selectedItemIds, activeGroupId } = useSelectedItems();
  const { items } = useAllItems();

  const allItems = React.useMemo(
    () =>
      tracks
        .filter((t) => !t.hidden)
        .map((t) => t.items)
        .flat(1)
        .filter((item) => {
          if (!items[item]?.type) return;

          return !hideOutlinesForItemTypes.includes(items[item].type);
        }),
    [items, tracks],
  );

  const activeGroupItemCandidate =
    activeGroupId == null ? null : items[activeGroupId];
  const activeGroupItem =
    activeGroupItemCandidate?.type === 'group'
      ? activeGroupItemCandidate
      : null;
  const activeGroupMemberId =
    activeGroupItem && selectedItemIds.length === 1
      ? activeGroupItem.children.includes(selectedItemIds[0] as string)
        ? selectedItemIds[0]
        : null
      : null;
  const outlineItemIds = React.useMemo(() => {
    const ids = allItems.filter(
      (itemId) => itemId !== activeGroupItem?.id || activeGroupMemberId == null,
    );
    if (activeGroupMemberId == null || ids.includes(activeGroupMemberId)) {
      return ids;
    }
    return [...ids, activeGroupMemberId];
  }, [activeGroupItem?.id, activeGroupMemberId, allItems]);

  const selectedItems = React.useMemo(
    () => outlineItemIds.filter((item) => selectedItemIds.includes(item)),
    [outlineItemIds, selectedItemIds],
  );

  const editMode = useContext(EditModeContext);
  const textItemEditing = useContext(TextItemEditingContext);

  const itemsToDisplay = React.useMemo(() => {
    if (textItemEditing) {
      return [textItemEditing];
    }

    if (
      editMode.editMode === 'draw-solid' ||
      editMode.editMode === 'draw-blur'
    ) {
      return selectedItems;
    }

    return outlineItemIds;
  }, [editMode.editMode, selectedItems, outlineItemIds, textItemEditing]);

  // 그룹을 한 덩어리로 선택한 상태(멤버 2개 이상)면 개별 outline 대신 union bbox 를 그린다.
  const isGroupBlockSelected =
    activeGroupId != null && selectedItemIds.length > 1;

  // 그룹 멤버에 hover 시 SelectionOutline 이 set 하는 그룹 id. 선택 상태가 아닐 때
  // 그룹 union 을 hover 표시로 보여준다.
  const [hoveredGroupId, setHoveredGroupId] = React.useState<string | null>(
    null,
  );
  const hoveredGroupMemberIds = React.useMemo(
    () =>
      hoveredGroupId != null
        ? allItems.filter((id) => items[id]?.groupId === hoveredGroupId)
        : [],
    [hoveredGroupId, allItems, items],
  );

  // 선택된 아이템(단일)의 핸들·크롭 버튼은 겹친 다른 아이템 위에서 클릭되어야 한다.
  // outline div(히트영역) 는 제자리에 두고(다른 아이템 클릭을 막지 않도록), 핸들 레이어만
  // DOM 최후미에서 다시 렌더한다. 여러 개 선택(핸들 미노출)이면 대상 없음.
  const elevatedHandleItemId =
    selectedItems.length === 1 ? selectedItems[0] : null;

  return (
    <>
      {itemsToDisplay
        .slice()
        .reverse()
        .map((itemId) => {
          const item = items[itemId];

          if (!item) return null;

          const geometry =
            item.type === 'group'
              ? resolveGroupGeometry(item, items)
              : item;

          return (
            <Sequence
              key={item.id}
              from={geometry.from}
              durationInFrames={geometry.durationInFrames}
              layout="none"
            >
              <SelectionOutline
                item={item}
                hoveredGroupId={hoveredGroupId}
                onHoverGroupChange={setHoveredGroupId}
                // 선택 아이템은 핸들 레이어를 이 위치에서 빼고 최상단 pass 로 넘긴다.
                phase={item.id === elevatedHandleItemId ? 'base' : 'all'}
              />
            </Sequence>
          );
        })}
      {elevatedHandleItemId != null &&
        items[elevatedHandleItemId] &&
        (() => {
          const item = items[elevatedHandleItemId];
          const geometry =
            item.type === 'group'
              ? resolveGroupGeometry(item, items)
              : item;
          return (
            <Sequence
              key={`${item.id}-handles`}
              from={geometry.from}
              durationInFrames={geometry.durationInFrames}
              layout="none"
            >
              <SelectionOutline
                item={item}
                hoveredGroupId={hoveredGroupId}
                onHoverGroupChange={setHoveredGroupId}
                phase="handles"
              />
            </Sequence>
          );
        })()}
      {isGroupBlockSelected && (
        <GroupSelectionOutline memberIds={selectedItemIds} />
      )}
      {!isGroupBlockSelected && hoveredGroupMemberIds.length > 1 && (
        <GroupSelectionOutline memberIds={hoveredGroupMemberIds} />
      )}
    </>
  );
};
